import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@sunstone/ui';
import { AlertTriangle, Loader2, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';

/** Minimal shape shared by both deployments.v1.BlockingResource and tenants.v1.BlockingResource */
export interface BlockingResourceInfo {
  id: string;
  displayName: string;
  resourceType: string;
}

interface DeleteConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resourceName: string;
  resourceType: 'workspace' | 'deployment' | 'customer' | 'partner';
  blockingResources?: BlockingResourceInfo[];
  supportsForceTombstone?: boolean;
  /** When set, the Delete button stays disabled until the user types this
   * exact string (case-sensitive) into a confirmation input. For workspace,
   * pass workspace.slug ("URL Name") rather than resourceName (displayName)
   * - the slug is the stable, unambiguous identifier operators already
   * associate with "the thing I'm about to delete." Omit entirely (as
   * deployment/customer/partner do today) to keep this dialog's original
   * behavior unchanged. */
  requireTypedConfirmation?: string;
  onConfirm: (mode: 'full' | 'force_tombstone') => void;
  isDeleting?: boolean;
}

const RESOURCE_LABELS: Record<string, string> = {
  workspace: 'workspace',
  deployment: 'deployment',
  customer: 'customer',
  partner: 'partner',
};

const BLOCKING_RESOURCE_LABELS: Record<string, string> = {
  workspace: 'Workspace',
  deployment: 'Deployment',
  tenant: 'Customer',
};

export function DeleteConfirmationDialog({
  open,
  onOpenChange,
  resourceName,
  resourceType,
  blockingResources = [],
  supportsForceTombstone = false,
  requireTypedConfirmation,
  onConfirm,
  isDeleting = false,
}: DeleteConfirmationDialogProps) {
  const [deleteMode, setDeleteMode] = useState<'full' | 'force_tombstone'>('full');
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const label = RESOURCE_LABELS[resourceType] ?? resourceType;
  const hasBlockers = blockingResources.length > 0;
  const confirmationMismatch =
    !!requireTypedConfirmation && typedConfirmation !== requireTypedConfirmation;

  // Reset the typed value whenever the dialog closes, so a re-open doesn't
  // start pre-filled with a stale value from a previous open.
  useEffect(() => {
    if (!open) setTypedConfirmation('');
  }, [open]);

  function handleConfirm() {
    onConfirm(deleteMode);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Delete {label}
          </DialogTitle>
          <DialogDescription>
            You are about to delete{' '}
            <span className="font-semibold text-foreground">{resourceName}</span>.
            {hasBlockers
              ? ' This action cannot proceed until all blocking resources are removed.'
              : ' This action cannot be undone.'}
          </DialogDescription>
        </DialogHeader>

        {hasBlockers ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The following resources must be deleted first:
            </p>
            <ul className="space-y-2">
              {blockingResources.map((r) => (
                <li key={r.id} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="text-xs shrink-0">
                    {BLOCKING_RESOURCE_LABELS[r.resourceType] ?? r.resourceType}
                  </Badge>
                  <span className="font-medium text-foreground">{r.displayName}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : supportsForceTombstone ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Choose how to delete this {label}:</p>
            <RadioGroup
              value={deleteMode}
              onValueChange={(v) => setDeleteMode(v as 'full' | 'force_tombstone')}
              className="space-y-3"
            >
              <div className="flex items-start gap-3 rounded-md border border-border p-3">
                <RadioGroupItem value="full" id="mode-full" className="mt-0.5" />
                <div>
                  <Label htmlFor="mode-full" className="font-medium cursor-pointer">
                    Full delete
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Tear down all cloud infrastructure before tombstoning the record. This is the
                    recommended option for most cases.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md border border-border p-3">
                <RadioGroupItem value="force_tombstone" id="mode-tombstone" className="mt-0.5" />
                <div>
                  <Label htmlFor="mode-tombstone" className="font-medium cursor-pointer">
                    Force tombstone
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Mark as deleted immediately without waiting for infrastructure teardown. Use
                    this only when infrastructure has already been manually cleaned up or does not
                    exist.
                  </p>
                </div>
              </div>
            </RadioGroup>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete{' '}
            <span className="font-semibold text-foreground">{resourceName}</span>? All associated
            data will be permanently removed.
          </p>
        )}

        {!hasBlockers && requireTypedConfirmation && (
          <div className="space-y-2">
            <Label htmlFor="delete-confirm-input" className="text-sm text-muted-foreground">
              Type{' '}
              <span className="font-mono font-semibold text-foreground">
                {requireTypedConfirmation}
              </span>{' '}
              to confirm.
            </Label>
            <Input
              id="delete-confirm-input"
              value={typedConfirmation}
              onChange={(e) => setTypedConfirmation(e.target.value)}
              autoComplete="off"
              disabled={isDeleting}
            />
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={hasBlockers || isDeleting || confirmationMismatch}
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Deleting…
              </>
            ) : (
              <>
                <Trash2 className="h-4 w-4" />
                Delete {label}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
